import type { Conversation, ConversationDraft, WorkspaceState } from "../types";

const KEY = "neurondeck-draft-checkpoint";
type TextDraft = Pick<ConversationDraft, "content" | "editingMessageId"> & { beforeEditContent?: string };
interface Checkpoint {
  id: string;
  updatedAt: string;
  draft: TextDraft;
}

export const readDraftCheckpoint = (): Map<string, Checkpoint> => {
  try {
    const entries: unknown = JSON.parse(sessionStorage.getItem(KEY) ?? "[]");
    return new Map(Array.isArray(entries) ? entries.filter(item => typeof item?.id === "string" && typeof item?.draft?.content === "string").map(item => [item.id, item]) : []);
  } catch { return new Map(); }
};

// IndexedDB work started during pagehide can be cancelled on navigation. This
// tab-scoped, synchronous fallback runs only when leaving, never on a keystroke.
// Attachment bodies stay in IndexedDB; the fallback protects native text edits.
export const checkpointDraftText = (workspace: WorkspaceState): void => {
  try {
    const entries: Checkpoint[] = workspace.conversations.filter(c => c.draft && c.messagesLoaded !== false).map(c => ({
      id: c.id, updatedAt: c.updatedAt,
      draft: { content: c.draft!.content, editingMessageId: c.draft!.editingMessageId, beforeEditContent: c.draft!.beforeEdit?.content },
    }));
    sessionStorage.setItem(KEY, JSON.stringify(entries));
  } catch { /* Normal IndexedDB persistence remains available if session storage is full/disabled. */ }
};

export const restoreDraftText = (conversation: Conversation, entries = readDraftCheckpoint()): Conversation => {
  try {
    const entry = entries.get(conversation.id);
    // Do not replay an old draft after the conversation changed in another tab.
    if (!entry || entry.updatedAt !== conversation.updatedAt || typeof entry.draft?.content !== "string") return conversation;
    const editingMessageId = entry.draft.editingMessageId;
    if (editingMessageId !== undefined && !conversation.messages.some(m => m.id === editingMessageId && m.role === "user")) return conversation;
    return { ...conversation, draft: {
      ...conversation.draft, content: entry.draft.content, attachments: conversation.draft?.attachments ?? [], editingMessageId,
      ...(typeof entry.draft.beforeEditContent === "string" ? { beforeEdit: {
        content: entry.draft.beforeEditContent, attachments: conversation.draft?.beforeEdit?.attachments ?? [],
      } } : { beforeEdit: undefined }),
    } };
  } catch { return conversation; }
};

export const clearDraftCheckpoint = (): void => {
  try { sessionStorage.removeItem(KEY); } catch { /* Browser storage can be restricted. */ }
};
